import { useLanguage } from '../contexts/LanguageContext';
import SharedHeader from './SharedHeader';
import Footer from './Footer';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: January 2025',
      sections: [
        {
          title: 'Information We Collect',
          content: [
            'QRCodeForge.com is committed to protecting your privacy. We collect minimal information to provide our services:',
            '• QR code generation data (temporarily stored in your browser only)',
            '• Usage analytics through Google Analytics (anonymized)',
            '• Technical information like browser type and device type for optimization',
            '• No personal information is required to use our QR code generator'
          ]
        },
        {
          title: 'How We Use Information',
          content: [
            'The information we collect is used solely to:',
            '• Generate QR codes according to your specifications',
            '• Improve our service performance and user experience',
            '• Analyze website usage patterns (anonymized data only)',
            '• Ensure website security and prevent abuse'
          ]
        },
        {
          title: 'Data Storage and Security',
          content: [
            '• All QR code data is processed locally in your browser',
            '• We do not store your QR code content on our servers',
            '• History is saved only in your browser\'s local storage',
            '• We use industry-standard security measures to protect our website',
            '• All communications are secured with HTTPS encryption'
          ]
        },
        {
          title: 'Third-Party Services',
          content: [
            'We use the following third-party services:',
            '• Google Analytics: For website usage analytics (anonymized data)',
            '• Google AdSense: For displaying relevant advertisements',
            '• These services may collect data according to their own privacy policies'
          ]
        },
        {
          title: 'Cookies and Local Storage',
          content: [
            '• We use local storage to save your QR code history and preferences',
            '• Google Analytics may use cookies for analytics purposes',
            '• You can clear your browser data at any time to remove stored information',
            '• Our service functions without cookies, but analytics may be affected'
          ]
        },
        {
          title: 'Your Rights',
          content: [
            'You have the right to:',
            '• Access, update, or delete your data (stored locally in your browser)',
            '• Opt out of analytics by using ad blockers or browser privacy settings',
            '• Request information about data processing',
            '• Contact us with privacy concerns'
          ]
        },
        {
          title: 'Children\'s Privacy',
          content: [
            '• Our service is not directed at children under 13',
            '• We do not knowingly collect personal information from children',
            '• If you believe a child has provided information, please contact us'
          ]
        },
        {
          title: 'Changes to Privacy Policy',
          content: [
            '• We may update this privacy policy from time to time',
            '• Changes will be posted on this page with an updated date',
            '• Continued use of our service constitutes acceptance of changes'
          ]
        },
        {
          title: 'Contact Information',
          content: [
            'For privacy-related questions or concerns, please contact us:',
            '• Email: support@qrcodeforge.com',
            '• Website: QRCodeForge.com',
            '• We are committed to addressing your privacy concerns promptly'
          ]
        }
      ]
    },
    zh: {
      title: '隐私政策',
      lastUpdated: '最后更新：2025年1月',
      sections: [
        {
          title: '我们收集的信息',
          content: [
            'QRCodeForge.com 致力于保护您的隐私。我们收集的信息极少，仅用于提供服务：',
            '• 二维码生成数据（仅临时存储在您的浏览器中）',
            '• 通过 Google Analytics 收集的匿名使用分析数据',
            '• 用于优化的技术信息，如浏览器类型和设备类型',
            '• 使用我们的二维码生成器无需提供个人信息'
          ]
        },
        {
          title: '信息使用方式',
          content: [
            '我们收集的信息仅用于：',
            '• 根据您的要求生成二维码',
            '• 改善服务性能和用户体验',
            '• 分析网站使用模式（仅匿名数据）',
            '• 确保网站安全并防止滥用'
          ]
        },
        {
          title: '数据存储和安全',
          content: [
            '• 所有二维码数据均在您的浏览器中本地处理',
            '• 我们不在服务器上存储您的二维码内容',
            '• 历史记录仅保存在您浏览器的本地存储中',
            '• 我们使用行业标准安全措施保护网站',
            '• 所有通信均使用 HTTPS 加密保护'
          ]
        },
        {
          title: '第三方服务',
          content: [
            '我们使用以下第三方服务：',
            '• Google Analytics：用于网站使用分析（匿名数据）',
            '• Google AdSense：用于显示相关广告',
            '• 这些服务可能根据其自身隐私政策收集数据'
          ]
        },
        {
          title: 'Cookies 和本地存储',
          content: [
            '• 我们使用本地存储来保存您的二维码历史和偏好设置',
            '• Google Analytics 可能出于分析目的使用 cookies',
            '• 您可以随时清除浏览器数据以删除存储的信息',
            '• 我们的服务在没有 cookies 的情况下也能正常运行，但分析功能可能受影响'
          ]
        },
        {
          title: '您的权利',
          content: [
            '您有权：',
            '• 访问、更新或删除您的数据（存储在您浏览器中的本地数据）',
            '• 通过广告拦截器或浏览器隐私设置退出分析',
            '• 请求有关数据处理的信息',
            '• 就隐私问题联系我们'
          ]
        },
        {
          title: '儿童隐私',
          content: [
            '• 我们的服务不面向 13 岁以下儿童',
            '• 我们不会故意收集儿童的个人信息',
            '• 如果您认为有儿童提供了信息，请联系我们'
          ]
        },
        {
          title: '隐私政策变更',
          content: [
            '• 我们可能会不时更新此隐私政策',
            '• 更改将发布在此页面上，并更新日期',
            '• 继续使用我们的服务即表示接受更改'
          ]
        },
        {
          title: '联系信息',
          content: [
            '如有隐私相关问题或疑虑，请联系我们：',
            '• 邮箱：support@qrcodeforge.com',
            '• 网站：QRCodeForge.com',
            '• 我们致力于及时解决您的隐私问题'
          ]
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Header */}
        <header className="pt-20 pb-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              {currentContent.title}
            </h1>
            <p className="text-gray-300">
              {currentContent.lastUpdated}
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 pb-16">
          <div className="space-y-8">
            {currentContent.sections.map((section, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <h2 className="text-2xl font-semibold text-white mb-4">
                  {section.title}
                </h2>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: paragraph
                          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
                          .replace(/• /g, '• ')
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPolicy;